import { Box, Dialog, Divider, IconButton, Stack, Typography, useTheme, type SxProps } from '@mui/material';
import { ArrowBackIosNew } from '@mui/icons-material';
import { type ReactNode, useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useBreakpointUp } from '@/hooks/use-breakpoint';

export interface ListDetailViewProps<T> {
  items: T[];
  renderListItem: (item: T, isSelected: boolean) => ReactNode;
  renderDetail: (item: T) => ReactNode;
  getItemKey: (item: T) => string | number;
  getItemTitle?: (item: T) => string;
  itemSubtitle?: string;
  initialSelectedItem?: T | null;
  listGridSize?: { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };
  detailGridSize?: { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };
  onItemSelect?: (item: T | null) => void;
  sx?: SxProps;
  headerActions?: (item: T) => ReactNode;
  hideListOnWideScreenWhenSelected?: boolean;
  disableItemSelection?: boolean; // If true, items won't be clickable and won't show detail view
}

export function ListDetailView<T>({
  items,
  renderListItem,
  renderDetail,
  getItemKey,
  itemSubtitle,
  getItemTitle = () => 'Detail View',
  initialSelectedItem = null,
  onItemSelect,
  sx,
  headerActions,
  hideListOnWideScreenWhenSelected = false,
  disableItemSelection = false,
}: ListDetailViewProps<T>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isWideScreen = useBreakpointUp('md');
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const lastSelectedKeyRef = useRef<string | number | null>(null);
  const isInitializingRef = useRef(false);

  const scrollbarStyles = {
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.grey[300],
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: theme.palette.grey[400],
    },
  };

  const handleSelectItem = (item: T | null) => {
    const itemKey = item ? getItemKey(item) : null;

    // Prevent duplicate calls if selecting the same item
    if (itemKey === lastSelectedKeyRef.current) {
      return;
    }

    lastSelectedKeyRef.current = itemKey;

    if (item && itemKey !== null) {
      searchParams.set('key', itemKey.toString());
    } else {
      searchParams.delete('key');
    }
    setSearchParams(searchParams);
    setSelectedItem(item);
    if (onItemSelect && !isInitializingRef.current) {
      onItemSelect(item);
    }
  };

  // Effect to handle URL-based selection - URL is the source of truth
  useEffect(() => {
    // Skip selection logic if item selection is disabled
    if (disableItemSelection) {
      setSelectedItem(null);
      return;
    }

    const keyFromUrl = searchParams.get('key');
    const currentKey = selectedItem ? getItemKey(selectedItem).toString() : null;

    // Prevent re-initialization if already selected
    if (keyFromUrl === currentKey && selectedItem !== null) {
      return;
    }

    isInitializingRef.current = true;

    if (keyFromUrl) {
      const itemFromUrl = items.find((item) => getItemKey(item).toString() === keyFromUrl);
      if (itemFromUrl) {
        const itemKey = getItemKey(itemFromUrl);
        if (itemKey !== lastSelectedKeyRef.current) {
          lastSelectedKeyRef.current = itemKey;
          // Use queueMicrotask to defer state updates
          queueMicrotask(() => {
            setSelectedItem(itemFromUrl);
            isInitializingRef.current = false;
            if (onItemSelect) {
              onItemSelect(itemFromUrl);
            }
          });
        } else {
          isInitializingRef.current = false;
        }
        return;
      }
    }

    // If no valid key in URL, only preselect on wide screens (unless hideListOnWideScreenWhenSelected is true)
    if (isWideScreen) {
      if (initialSelectedItem && items.includes(initialSelectedItem)) {
        const itemKey = getItemKey(initialSelectedItem);
        if (itemKey !== lastSelectedKeyRef.current) {
          lastSelectedKeyRef.current = itemKey;
          queueMicrotask(() => {
            setSelectedItem(initialSelectedItem);
            searchParams.set('key', itemKey.toString());
            setSearchParams(searchParams);
            isInitializingRef.current = false;
            if (onItemSelect) {
              onItemSelect(initialSelectedItem);
            }
          });
        } else {
          isInitializingRef.current = false;
        }
      } else if (items.length > 0 && !hideListOnWideScreenWhenSelected) {
        // Select first item if no initial selection and items exist (only if not hiding list)
        const firstItem = items[0];
        const itemKey = getItemKey(firstItem);
        if (itemKey !== lastSelectedKeyRef.current) {
          lastSelectedKeyRef.current = itemKey;
          queueMicrotask(() => {
            setSelectedItem(firstItem);
            searchParams.set('key', itemKey.toString());
            setSearchParams(searchParams);
            isInitializingRef.current = false;
            if (onItemSelect) {
              onItemSelect(firstItem);
            }
          });
        } else {
          isInitializingRef.current = false;
        }
      } else {
        // No items available or hideListOnWideScreenWhenSelected is true, clear selection
        if (lastSelectedKeyRef.current !== null) {
          lastSelectedKeyRef.current = null;
          queueMicrotask(() => {
            setSelectedItem(null);
            searchParams.delete('key');
            setSearchParams(searchParams);
            isInitializingRef.current = false;
            if (onItemSelect) {
              onItemSelect(null);
            }
          });
        } else {
          isInitializingRef.current = false;
        }
      }
    } else {
      if (lastSelectedKeyRef.current !== null) {
        lastSelectedKeyRef.current = null;
        queueMicrotask(() => {
          setSelectedItem(null);
          isInitializingRef.current = false;
          if (onItemSelect) {
            onItemSelect(null);
          }
        });
      } else {
        isInitializingRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    items,
    searchParams.get('key'),
    initialSelectedItem,
    isWideScreen,
    hideListOnWideScreenWhenSelected,
    disableItemSelection,
    getItemKey,
    setSearchParams,
  ]);

  // Effect to keep selectedItem in sync when items change (e.g., after mutations)
  useEffect(() => {
    if (selectedItem) {
      const currentKey = getItemKey(selectedItem);
      const updatedItem = items.find((item) => getItemKey(item) === currentKey);
      if (updatedItem && updatedItem !== selectedItem) {
        // Only update if the item reference actually changed
        queueMicrotask(() => {
          setSelectedItem(updatedItem);
          // Don't call onItemSelect here as the selection hasn't changed, only the item reference
        });
      } else if (!updatedItem) {
        // Item was removed, clear selection
        lastSelectedKeyRef.current = null;
        queueMicrotask(() => {
          setSelectedItem(null);
          searchParams.delete('key');
          setSearchParams(searchParams);
          if (onItemSelect && !isInitializingRef.current) {
            onItemSelect(null);
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, getItemKey, setSearchParams]);

  return (
    <Box
      sx={{
        ...(isWideScreen && {
          height: '70vh',
          display: 'flex',
          gap: 2,
          minHeight: 0,
        }),
        ...sx,
      }}
    >
      {(!isWideScreen || !selectedItem || !hideListOnWideScreenWhenSelected) && (
        <Box
          sx={{
            ...(isWideScreen && {
              flex: hideListOnWideScreenWhenSelected && !selectedItem ? '1 1 100%' : '0 0 350px',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }),
          }}
        >
          <Box
            sx={{
              ...(isWideScreen && {
                flex: 1,
                overflowY: 'auto',
                minHeight: 0,
                ...scrollbarStyles,
              }),
            }}
          >
            {items.map((item) => (
              <Box
                key={getItemKey(item)}
                onClick={disableItemSelection ? undefined : () => handleSelectItem(item)}
                sx={disableItemSelection ? { mb: 1.5 } : { cursor: 'pointer', mb: 1.5 }}
              >
                {renderListItem(item, selectedItem ? getItemKey(item) === getItemKey(selectedItem) : false)}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {isWideScreen && selectedItem && (
        <>
          <Divider orientation="vertical" flexItem />
          <Box
            sx={{
              flex: '1',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                ...scrollbarStyles,
              }}
            >
              {renderDetail(selectedItem)}
            </Box>
          </Box>
        </>
      )}

      {!isWideScreen && selectedItem && (
        <Dialog
          fullScreen
          open={!!selectedItem}
          onClose={() => handleSelectItem(null)}
          sx={{ '.MuiPaper-root': { backgroundImage: 'none' } }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ borderBottom: 1, borderColor: 'divider', py: 1 }}
          >
            <IconButton size="small" onClick={() => handleSelectItem(null)} sx={{ pl: '8px' }}>
              <ArrowBackIosNew sx={{ height: 24, width: 24 }} />
            </IconButton>
            <Stack alignItems="start" sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'normal',
                  maxHeight: '3em',
                }}
              >
                {typeof selectedItem === 'object' && selectedItem !== null ? getItemTitle(selectedItem) : 'Detail View'}
              </Typography>
              <Typography variant="subtitle1" color="textDisabled">
                {itemSubtitle}
              </Typography>
            </Stack>
            {headerActions && selectedItem && <Box sx={{ pr: 2 }}>{headerActions(selectedItem)}</Box>}
          </Stack>
          <Box sx={{ m: '0 1rem' }}>{renderDetail(selectedItem)}</Box>
        </Dialog>
      )}
    </Box>
  );
}
